import React, { useState } from 'react';
import { 
  X, 
  Sparkles, 
  Calendar, 
  Send, 
  Building2, 
  Mail, 
  Phone, 
  User, 
  Users, 
  CheckCircle2, 
  ShieldCheck, 
  ExternalLink, 
  Clock, 
  MessageSquare, 
  ArrowRight,
  Landmark,
  FileCheck,
  Zap,
  Copy,
  Check
} from 'lucide-react';
import { db } from '../lib/firebase';
import { collection, doc, setDoc } from 'firebase/firestore';
import { logAuditAction } from '../utils/auditLogger';

export type DemoInquiryType = 'vip_1on1' | 'bank_pilot' | 'enterprise_sponsor' | 'cipc_sars_partner' | 'accelerator_pilot';

export interface DemoRequestData {
  requestId: string;
  fullName: string;
  workEmail: string;
  companyName: string;
  phone: string;
  inquiryType: DemoInquiryType;
  cohortSize: string;
  focusArea: string;
  preferredDateTime: string;
  notes: string;
  createdAt: string;
  status: 'new' | 'contacted' | 'scheduled' | 'completed';
}

export interface RequestDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultInquiryType?: DemoInquiryType;
  showToast?: (msg: string, type?: 'info' | 'success') => void;
  onNavigateToBooking?: () => void;
  onOpenInteractiveTour?: () => void;
}

export function RequestDemoModal({
  isOpen,
  onClose,
  defaultInquiryType = 'vip_1on1',
  showToast,
  onNavigateToBooking,
  onOpenInteractiveTour
}: RequestDemoModalProps) {
  const [inquiryType, setInquiryType] = useState<DemoInquiryType>(defaultInquiryType);
  const [fullName, setFullName] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [cohortSize, setCohortSize] = useState('1 (Solo Founder)');
  const [focusArea, setFocusArea] = useState('Live Profit Margin & Real-Time Cash Flow');
  const [preferredDateTime, setPreferredDateTime] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedData, setSubmittedData] = useState<DemoRequestData | null>(null);
  const [copiedRef, setCopiedRef] = useState(false);

  // Sync inquiryType when defaultInquiryType changes
  React.useEffect(() => {
    if (defaultInquiryType) {
      setInquiryType(defaultInquiryType);
    }
  }, [defaultInquiryType]);

  if (!isOpen) return null;

  const triggerToast = (msg: string, type?: 'info' | 'success') => {
    if (showToast) {
      showToast(msg, type);
    } else {
      console.log(`[Toast ${type || 'info'}]: ${msg}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fullName.trim() || !workEmail.trim() || !companyName.trim() || !phone.trim()) {
      triggerToast('⚠️ Please fill in all required contact details.', 'info');
      return;
    }

    // Basic email validation
    if (!workEmail.includes('@') || !workEmail.includes('.')) {
      triggerToast('⚠️ Please enter a valid email address.', 'info');
      return;
    }

    setIsSubmitting(true);

    const randomSuffix = Math.floor(100000 + Math.random() * 900000);
    const prefix = inquiryType === 'bank_pilot' ? 'FNB-PILOT' : inquiryType === 'enterprise_sponsor' ? 'ESD-SPONSOR' : 'VIP-DEMO';
    const requestId = `${prefix}-${randomSuffix}`;
    const nowIso = new Date().toISOString();

    const payload: DemoRequestData = {
      requestId,
      fullName: fullName.trim(),
      workEmail: workEmail.trim().toLowerCase(),
      companyName: companyName.trim(),
      phone: phone.trim(),
      inquiryType,
      cohortSize,
      focusArea,
      preferredDateTime: preferredDateTime.trim() || 'Flexible / Next Available Slot',
      notes: notes.trim(),
      createdAt: nowIso,
      status: 'new'
    };

    try {
      // 1. Save to Firestore
      try {
        const docRef = doc(db, 'demo_requests', requestId);
        await setDoc(docRef, payload);
      } catch (firestoreErr) {
        console.warn('Firestore direct write notice (falling back to local queue):', firestoreErr);
      }

      // 2. Save to localStorage queue for offline redundancy & admin sync
      try {
        const existingQueue = JSON.parse(localStorage.getItem('azuraos_demo_requests_queue') || '[]');
        const updatedQueue = [payload, ...existingQueue];
        localStorage.setItem('azuraos_demo_requests_queue', JSON.stringify(updatedQueue));
      } catch (localErr) {
        console.warn('Local storage queue write notice:', localErr);
      }

      // 3. Log Audit Action
      logAuditAction({
        actionType: 'SUBMIT_DEMO_REQUEST',
        entityType: 'System',
        entityId: requestId,
        details: `Submitted Demo & Pilot Request [${requestId}] by ${fullName} (${companyName})`,
        performedBy: workEmail
      });

      setSubmittedData(payload);
      triggerToast(`🎉 Demo request #${requestId} submitted successfully!`, 'success');
    } catch (err) {
      console.error('Error submitting demo request:', err);
      triggerToast('⚠️ Submission queued. We will reach out promptly.', 'info');
      setSubmittedData(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyReference = () => {
    if (submittedData) {
      navigator.clipboard.writeText(submittedData.requestId);
      setCopiedRef(true);
      showToast('Reference ID copied to clipboard.', 'success');
      setTimeout(() => setCopiedRef(false), 2500);
    }
  };

  const handleOpenDirectEmail = () => {
    if (!submittedData) return;
    const subject = encodeURIComponent(`[${submittedData.requestId}] AzuraOS Live Demo & Pilot Inquiry — ${submittedData.companyName}`);
    const body = encodeURIComponent(
      `Hi AzuraOS Founder & Team,\n\nI have requested a live walkthrough/pilot exploration for ${submittedData.companyName}.\n\nReference ID: ${submittedData.requestId}\nInquiry Type: ${submittedData.inquiryType.toUpperCase()}\nPreferred Time: ${submittedData.preferredDateTime}\nPhone/WhatsApp: ${submittedData.phone}\n\nNotes/Goals:\n${submittedData.notes || 'Looking forward to the live operational walkthrough.'}\n\nBest regards,\n${submittedData.fullName}\n${submittedData.workEmail}`
    );
    window.open(`mailto:demorequest@azuraos.app?cc=support@azuraos.app&subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity animate-fade-in"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-2xl max-h-[92vh] bg-[#151819] border border-[#1F2426] rounded-2xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden z-10 animate-in fade-in zoom-in-95 duration-200 text-gray-200 my-auto">
        
        {/* Sticky Header */}
        <div className="sticky top-0 z-30 bg-[#121516]/95 backdrop-blur-md p-4 sm:p-5 border-b border-[#1F2426] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] flex items-center justify-center shadow-inner shrink-0">
              <Sparkles size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm sm:text-lg font-display font-bold text-white tracking-tight">
                  Request VIP Demo &amp; Cohort Pilot Walkthrough
                </h2>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-[#14B8A6]/20 text-[#14B8A6] rounded border border-[#14B8A6]/30">
                  LIVE SESSION
                </span>
              </div>
              <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5">
                Direct screen-share with the founder or sponsor a 30-day SME cohort pilot.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 rounded-xl hover:bg-[#1F2426] transition cursor-pointer shrink-0"
            title="Close modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        {submittedData ? (
          /* Confirmation State */
          <div className="p-6 sm:p-8 space-y-6 text-center animate-fade-in overflow-y-auto custom-scrollbar flex-1">
            <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-center text-emerald-400 mx-auto shadow-lg">
              <CheckCircle2 size={32} />
            </div>

            <div className="space-y-2">
              <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                REQUEST LOGGED IN FOUNDER QUEUE
              </span>
              <h3 className="font-display font-bold text-2xl text-white">
                You're on the VIP Priority List!
              </h3>
              <p className="text-xs sm:text-sm text-gray-300 max-w-md mx-auto leading-relaxed">
                Thank you, <strong className="text-white">{submittedData.fullName}</strong>. Your request for <strong className="text-teal-300">{submittedData.companyName}</strong> has been assigned reference ID:
              </p>
            </div>

            {/* Reference ID Card */}
            <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-4 max-w-md mx-auto flex items-center justify-between gap-3">
              <div className="text-left font-mono">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider block">Reference Code</span>
                <span className="text-base font-bold text-teal-400">{submittedData.requestId}</span>
              </div>
              <button
                type="button"
                onClick={handleCopyReference}
                className="px-3 py-1.5 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-200 text-xs font-bold rounded-lg border border-gray-700 transition flex items-center gap-1.5 cursor-pointer"
              >
                {copiedRef ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                <span>{copiedRef ? 'Copied' : 'Copy'}</span>
              </button>
            </div>

            <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-xl text-left max-w-md mx-auto text-xs text-gray-300 space-y-2">
              <div className="flex items-center gap-2 text-teal-400 font-bold">
                <Zap size={14} />
                <span>Next Immediate Steps:</span>
              </div>
              <ul className="space-y-1.5 list-disc pl-4 text-gray-400">
                <li>We will reach out via WhatsApp/Phone (<strong className="text-gray-200">{submittedData.phone}</strong>) or Email within a few hours to confirm meeting coordinates.</li>
                <li>You will receive a calendar screen-share link (Google Meet / Zoom).</li>
                <li>Direct routing alert has been sent to <strong className="text-gray-200 font-mono">demorequest@azuraos.app</strong>.</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              {onNavigateToBooking && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onNavigateToBooking();
                  }}
                  className="w-full sm:w-auto px-5 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/20 transition flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Calendar size={14} />
                  <span>Pick a Live Slot on Public Calendar Now</span>
                  <ArrowRight size={14} />
                </button>
              )}

              <button
                type="button"
                onClick={handleOpenDirectEmail}
                className="w-full sm:w-auto px-4 py-3 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 hover:text-white font-bold text-xs rounded-xl border border-gray-700 transition flex items-center justify-center gap-2 cursor-pointer"
              >
                <Mail size={14} />
                <span>Send Quick Note via Email</span>
                <ExternalLink size={12} />
              </button>
            </div>
          </div>
        ) : (
          /* Form Entry State */
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5 overflow-y-auto custom-scrollbar flex-1">
            
            {/* Category Selector Tabs */}
            <div className="space-y-2">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block">
                Select Your Inquiry Archetype
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {[
                  {
                    id: 'accelerator_pilot',
                    label: 'Accelerator & Cohort',
                    desc: 'Batch runway & solvency radar',
                    icon: Users
                  },
                  {
                    id: 'enterprise_sponsor',
                    label: 'ESD & Corporate',
                    desc: 'Supplier development funding',
                    icon: Building2
                  },
                  {
                    id: 'bank_pilot',
                    label: 'Bank & Innovation',
                    desc: 'Sponsor a 30-day cohort pilot',
                    icon: Landmark
                  },
                  {
                    id: 'vip_1on1',
                    label: 'Founder Walkthrough',
                    desc: 'Live 1-on-1 architecture tour',
                    icon: User
                  }
                ].map((tab) => {
                  const Icon = tab.icon;
                  const isSelected = inquiryType === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setInquiryType(tab.id as DemoInquiryType)}
                      className={`p-3 rounded-xl border text-left transition flex flex-col justify-between space-y-1.5 cursor-pointer ${
                        isSelected
                          ? 'bg-[#14B8A6]/10 border-[#14B8A6] text-white shadow-md shadow-[#14B8A6]/5'
                          : 'bg-[#0C0E0F] border-[#1F2426] text-gray-400 hover:text-gray-200 hover:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <Icon size={16} className={isSelected ? 'text-[#14B8A6]' : 'text-gray-500'} />
                        {isSelected && <span className="w-2 h-2 rounded-full bg-[#14B8A6] animate-pulse" />}
                      </div>
                      <div>
                        <div className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                          {tab.label}
                        </div>
                        <div className="text-[10px] text-gray-500 leading-tight">
                          {tab.desc}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Inputs Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              {/* Full Name */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1.5">
                  <User size={12} className="text-[#14B8A6]" />
                  <span>Full Name *</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Lerato Khumalo"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6]"
                />
              </div>

              {/* Work / Business Email */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1.5">
                  <Mail size={12} className="text-[#14B8A6]" />
                  <span>Work / Business Email *</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="e.g. founder@company.co.za"
                  value={workEmail}
                  onChange={(e) => setWorkEmail(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6]"
                />
              </div>

              {/* Company / Brand Name */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1.5">
                  <Building2 size={12} className="text-[#14B8A6]" />
                  <span>Company / Organization Name *</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Studio 96 / FNB Business Banking"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6]"
                />
              </div>

              {/* WhatsApp / Phone */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1.5">
                  <Phone size={12} className="text-[#14B8A6]" />
                  <span>WhatsApp / Phone Number *</span>
                </label>
                <input
                  type="tel"
                  required
                  placeholder="e.g. +27 82 123 4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6]"
                />
              </div>

              {/* Cohort / Team Size */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1.5">
                  <Users size={12} className="text-[#14B8A6]" />
                  <span>Target SME / User Cohort Size</span>
                </label>
                <select
                  value={cohortSize}
                  onChange={(e) => setCohortSize(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#14B8A6]"
                >
                  <option value="1 (Solo Founder)">1 (Solo Operator / Freelancer)</option>
                  <option value="2-10 (Small Agency / Studio)">2-10 (Small Studio / Micro-Enterprise)</option>
                  <option value="10-25 (Pilot Cohort)">10-25 (Bank / Incubator Pilot Cohort)</option>
                  <option value="25-50 (ESD Program)">25-50 (ESD Supplier Development Cohort)</option>
                  <option value="50+ (Enterprise / Institutional)">50+ (National Institutional Program)</option>
                </select>
              </div>

              {/* Preferred Date/Time */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1.5">
                  <Clock size={12} className="text-[#14B8A6]" />
                  <span>Preferred Date / Time Window &amp; Timezone</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Friday 10:00 AM (SAST / UTC / EST / GMT)"
                  value={preferredDateTime}
                  onChange={(e) => setPreferredDateTime(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6]"
                />
              </div>
            </div>

            {/* Primary Focus Area */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1.5">
                <Zap size={12} className="text-[#14B8A6]" />
                <span>Primary Focus Area for the Walkthrough</span>
              </label>
              <select
                value={focusArea}
                onChange={(e) => setFocusArea(e.target.value)}
                className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#14B8A6]"
              >
                <option value="Live Profit Margin & Real-Time Cash Flow">Live Profit Margin Defense &amp; Real-Time Cash Flow</option>
                <option value="Global Multi-Currency & Local Tax Reserves">Global Multi-Currency ($, R, £, €) &amp; Local Tax Reserves</option>
                <option value="SME Bankruptcy Prevention & Solvency Modeling">SME Bankruptcy Prevention &amp; Solvency Modeling</option>
                <option value="Public Bookings, Invoicing & VAT Compliance">Client Bookings (/book), Automated Invoicing &amp; VAT/GST</option>
                <option value="Private AI Co-Founder & Strategy Engine">Private AI Co-Founder (Strictly BYOK / Gemini)</option>
                <option value="Institutional Sponsor / Pilot Cohort Integration">Institutional Bank / ESD Sponsor Cohort Integration</option>
              </select>
            </div>

            {/* Notes / Special Requests */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-300 flex items-center gap-1.5">
                <MessageSquare size={12} className="text-[#14B8A6]" />
                <span>Specific Questions or Goals (Optional)</span>
              </label>
              <textarea
                rows={2}
                placeholder="Tell us about your business setup or what specific questions you'd like the founder to address..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] resize-none"
              />
            </div>

            {/* Guarantee Reassurance Strip */}
            <div className="p-3 bg-[#0C0E0F] border border-[#1F2426] rounded-xl flex items-center justify-between gap-2 text-[11px] text-gray-400">
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-teal-400 shrink-0" />
                <span>100% Confidential • Zero Spam • No Credit Card Required</span>
              </div>
              <span className="font-mono text-gray-500">SAST Timezone</span>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 text-xs font-bold rounded-xl transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] disabled:opacity-50 text-black font-extrabold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/20 transition flex items-center gap-2 cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    <span>Booking VIP Slot...</span>
                  </>
                ) : (
                  <>
                    <Send size={13} />
                    <span>Submit VIP Demo Request</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}

      </div>
    </div>
  );
}
